import React, { useCallback, useContext, useMemo } from 'react';
import { useFragment } from 'react-relay/hooks';

import { createStyles, ListItemAvatar, Theme, withStyles, WithStyles } from '@material-ui/core';
import ListItem, { ListItemProps } from '@material-ui/core/ListItem';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { compact, concat, filter, get, includes } from 'lodash';

import { Checkbox } from 'components/v2';
import { fromGlobalId } from 'services/relayService';

import { ListItemNode_Node$key } from './__generated__/ListItemNode_Node.graphql';
import AvatarNode from './AvatarNode';
import ListNodesContext from './ListNodesContext';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    buttonWithoutHover: {
      '&:hover': {
        backgroundColor: 'initial',
      },
      '&:focus': {
        backgroundColor: 'initial',
      },
    },
    checkboxRoot: {
      paddingTop: 3,
      paddingBottom: 3,
      marginLeft: -1 * unit * 2.5,
    },
  });

export interface ListItemNodeProps extends Omit<ListItemProps, 'classes'>, WithStyles<typeof styles> {
  fragmentRef: ListItemNode_Node$key;
  includeAvatar?: boolean;
  disabled?: boolean;
}

const ListItemNode: React.FC<ListItemNodeProps> = ({
  classes,
  className,
  fragmentRef,
  children,
  includeAvatar,
  onClick,
  disabled = false,
  ...rest
}) => {
  const initialNode = useFragment<ListItemNode_Node$key>(
    graphql`
      fragment ListItemNode_Node on Node @argumentDefinitions(includeAvatar: { type: "Boolean", defaultValue: false }) {
        __typename
        id
        ... on User {
          ...AvatarNode_Node @include(if: $includeAvatar)
          name
        }
        ... on Provider {
          ...AvatarNode_Node @include(if: $includeAvatar)
          name
        }
        ... on Patient {
          ...AvatarNode_Node @include(if: $includeAvatar)
          name
        }
        ... on Group {
          ...AvatarNode_Node @include(if: $includeAvatar)
          # Using alias bc of conflicting types String! and String.
          groupName: name
        }
        ... on Audit {
          ...AvatarNode_Node @include(if: $includeAvatar)
          # Can't use name since the type is different
          nameJson: new
        }
      }
    `,
    fragmentRef,
  );

  const node = useMemo(
    () => ({
      ...initialNode,
      _id: fromGlobalId(initialNode.id).id,
      name: initialNode.name || get(initialNode, 'groupName'),
    }),
    [initialNode],
  );
  const { idsSelected, idsChecked, onNodeSelected, onNodesChecked } = useContext(ListNodesContext);

  const selected = useMemo(() => includes(idsSelected, node._id), [idsSelected, node._id]);

  const checked = useMemo(() => includes(idsChecked, node._id), [idsChecked, node._id]);

  const handleNodeChecked = useCallback(
    (event: React.UIEvent | React.SyntheticEvent) => {
      if (!onNodesChecked) {
        return;
      }
      const newIdsChecked = checked ? filter(idsChecked, (_id) => _id !== node._id) : concat(idsChecked, node._id);
      onNodesChecked(event, compact(newIdsChecked));
    },
    [node, checked, idsChecked, onNodesChecked],
  );

  return (
    <ListItem
      disabled={disabled}
      classes={{
        root: className,
        button: classnames({
          [classes.buttonWithoutHover]: onNodesChecked && !onNodeSelected,
        }),
      }}
      button={!!onNodeSelected || !!onNodesChecked}
      selected={selected}
      onClick={(event) => {
        if (onClick) {
          onClick(event);
        }
        if (onNodeSelected) {
          onNodeSelected(event, node);
        } else if (onNodesChecked) {
          handleNodeChecked(event);
        }
      }}
      {...rest}
    >
      {onNodesChecked && (
        <Checkbox
          disabled={disabled}
          classes={{ root: classes.checkboxRoot }}
          color="primary"
          size="small"
          tabIndex={-1}
          disableRipple
          checked={checked}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => handleNodeChecked(event)}
          inputProps={{
            'data-lh-id': 'filter-facilities-list-item-node-checkbox',
          }}
        />
      )}
      {includeAvatar && (
        <ListItemAvatar {...rest}>
          <AvatarNode fragmentRef={node} size="small" />
        </ListItemAvatar>
      )}
      {children}
    </ListItem>
  );
};

export default withStyles(styles)(ListItemNode);
